import styles from "../../pages/CodesList.module.css";
import React from "react";
import DeleteIcon from "../icons/DeleteIcon";
import DownloadIcon from "../icons/DownloadIcon";

interface AddCodesModalProps {
  show: boolean;
  addCodesMode: "excel" | "manual";
  setAddCodesMode: (mode: "excel" | "manual") => void;
  excelFile: File | null;
  setExcelFile: (file: File | null) => void;
  manualCodesList: { code: string; codeValue: string }[];
  setManualCodesList: (list: { code: string; codeValue: string }[]) => void;
  addCodesResult: { added: string[]; alreadyExists: string[]; error?: string } | null;
  setAddCodesResult: (result: { added: string[]; alreadyExists: string[]; error?: string } | null) => void;
  addCodesLoading: boolean;
  handleAddCodesSubmit: () => void;
  setShowAddCodesModal: (show: boolean) => void;
  parseExcelFile: (file: File) => Promise<{ code: string; codeValue: string }[]>;
  ExcelIcon: React.FC;
}

const AddCodesModal: React.FC<AddCodesModalProps> = ({
  show,
  addCodesMode,
  setAddCodesMode,
  excelFile,
  setExcelFile,
  manualCodesList,
  setManualCodesList,
  addCodesResult,
  setAddCodesResult,
  addCodesLoading,
  handleAddCodesSubmit,
  setShowAddCodesModal,
  parseExcelFile,
  ExcelIcon,
}) => {
  if (!show) return null;
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} style={{ minWidth: 350 }}>
        <h3 className={styles.modalTitle}>הוסף מספרים</h3>
        <div className={styles.addCodesModeButtons}>
          <button
            className={
              addCodesMode === "excel"
                ? styles.selectedSortButton
                : styles.sortButton
            }
            onClick={() => setAddCodesMode("excel")}
          >
            העלה קובץ אקסל
          </button>
          <button
            className={
              addCodesMode === "manual"
                ? styles.selectedSortButton
                : styles.sortButton
            }
            onClick={() => setAddCodesMode("manual")}
          >
            הזן מספרים ידנית
          </button>
        </div>
        {addCodesMode === "excel" ? (
          <div className={styles.addCodesInput} style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <label className={styles.addCodesInputLabel} htmlFor="excelFile">
              בחר קובץ אקסל
              <ExcelIcon />
            </label>
            <a
              href={`${process.env.REACT_APP_BACKEND || ''}/download/sample-fuel-codes`}
              download
              className={styles.downloadLink}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              הורד דוגמה
              <DownloadIcon />
            </a>
            <input
              id="excelFile"
              hidden
              type="file"
              accept=".xls,.xlsx"
              onChange={async (e) => {
                const file = e.target.files?.[0] || null;
                setExcelFile(file);
                if (file) {
                  try {
                    const codes = await parseExcelFile(file);
                    setManualCodesList(
                      codes.map((c) => ({
                        code: c.code,
                        codeValue: c.codeValue,
                      }))
                    );
                    setAddCodesMode("manual");
                  } catch (err) {
                    setManualCodesList([{ code: "", codeValue: "" }]);
                  }
                }
              }}
            />
          </div>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              <span>הוסף מספרים (מספר וערך לכל שורה):</span>
            </div>
            <div className={styles.manualCodesListContainer}>
              {manualCodesList.map((row, idx) => (
                <div
                  key={idx}
                  style={{ display: "flex", gap: 8, marginBottom: 4 }}
                >
                  <input
                    type="text"
                    placeholder="מספר"
                    value={row.code}
                    onChange={(e) => {
                      const newList = [...manualCodesList];
                      newList[idx].code = e.target.value;
                      setManualCodesList(newList);
                    }}
                    style={{ flex: 2 }}
                  />
                  <input
                    type="text"
                    placeholder="ערך"
                    value={row.codeValue}
                    onChange={(e) => {
                      const newList = [...manualCodesList];
                      newList[idx].codeValue = e.target.value;
                      setManualCodesList(newList);
                    }}
                    style={{ flex: 2 }}
                  />
                  <div
                    onClick={
                      manualCodesList.length > 1
                        ? () => {
                            setManualCodesList(manualCodesList.filter((_, i) => i !== idx));
                          }
                        : undefined
                    }
                    style={{
                      color:
                        manualCodesList.length > 1 ? "red" : "lightgray",
                    }}
                    className={styles.removeRowButton}
                  >
                    <DeleteIcon />
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() =>
                setManualCodesList([
                  ...manualCodesList,
                  { code: "", codeValue: "" },
                ])
              }
              style={{ marginTop: 8 }}
            >
              הוסף שורה
            </button>
          </div>
        )}
        {addCodesResult && (
          <div
            style={{
              marginBottom: 12,
              color: addCodesResult.error ? "red" : "green",
            }}
          >
            {addCodesResult.error ? (
              <span>{addCodesResult.error}</span>
            ) : (
              <>
                <div>
                  מספרים שנוספו: {addCodesResult.added.join(", ") || "אין"}
                </div>
                <div>
                  מספרים שכבר קיימים: {addCodesResult.alreadyExists.join(", ") || "אין"}
                </div>
              </>
            )}
          </div>
        )}
        <div className={styles.modalActions}>
          <button
            className={styles.cancelButton}
            onClick={() => {
              setShowAddCodesModal(false);
              setExcelFile(null);
              setManualCodesList([{ code: "", codeValue: "" }]);
              setAddCodesResult(null);
            }}
            disabled={addCodesLoading}
          >
            {addCodesResult ? "סגור" : "ביטול"}
          </button>
          <button
            className={styles.updateButton}
            onClick={handleAddCodesSubmit}
            disabled={addCodesLoading}
          >
            {addCodesLoading ? "מוסיף..." : "הוסף"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCodesModal; 